import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { GetLabOrder } from "../../services/OrderService";
import LabOrderOverview from "./LabOrderOverView";
import LabOrderActions from "./LabOrderActions";
import OrderBillingView from "./OrderBillingView";
import OrderReportingView from "./OrderReportingView";
import SamplesTable from "../samples/SamplesTable";
import ResultsTable from "../results/ResultsTable";
import EditResultsTable from "../results/EditResultsTable"
import CommonLoader from "../common/CommonLoader";
import { NotificationManager } from "react-notifications";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import OrderAttachmentsTable from "../lab-orders/OrderAttachmentsTable";
import EditOrderInfo from "./EditOrderInfo";

import { IconButton, Tooltip, Chip, Box, Button } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

const EditLabOrder = () => {
  const [loading, setLoading] = useState(false);
  const [labOrderData, setLabOrderData] = useState(null);
  const [actionsExpanded, setActionsExpanded] = useState(false);
  const { orderNumber } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    getLabOrder(orderNumber);
  }, [orderNumber]);

  const getLabOrder = async (orderNumber) => {
    try {
      setLoading(true);
      let response = await GetLabOrder(orderNumber);      
      setLabOrderData(response.results);
    } catch (err) {
      NotificationManager.error("Error getting order. " + err, "Error");
    } finally {
      setLoading(false);
    }
  };

  const handleViewInvoiceClick = (invoiceNumber) => {
    const orderUrl = `/vlp/invoices/${invoiceNumber}`;
    if (window.invoiceViewWindow && !window.invoiceViewWindow.closed) {
      window.invoiceViewWindow.location.href = orderUrl;
    } else {
      window.invoiceViewWindow = window.open(
        orderUrl,
        "_blank",
        "resizable=yes,scrollbars=yes"
      );
    }
  };
  const handleOrderUpdate = (updatedOrder) => {
    setLabOrderData(updatedOrder);
  };

  // Merges server-side order changes (e.g. a slate moving the due date) into
  // the loaded order so the order form shows them.
  const handleOrderFieldsChanged = (changedFields) => {
    setLabOrderData((prev) => (prev ? { ...prev, ...changedFields } : prev));
  };

  const goToNextOrder = () => {
    const nextOrderNumber = parseInt(orderNumber) + 1;
    navigate(`/vlp/lab-orders/${nextOrderNumber}`);
  };

  const goToPreviousOrder = () => {
    const previousOrderNumber = parseInt(orderNumber) - 1;
    navigate(`/vlp/lab-orders/${previousOrderNumber}`);
  };

  return (
    <div className="vlp-component-container" style={{ maxWidth: "100%", overflowX: "hidden" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span className="title">
          Edit Lab Order #{" "}
          <span style={{ color: "rgb(25,118,210)", fontWeight: "bold" }}>
            {orderNumber}
          </span>
        </span>

        <div className="order-navigation-buttons" style={{ display: "flex" }}>
          <Tooltip title="Move to Previous Order">
            <IconButton onClick={goToPreviousOrder}>
              <ArrowBackRoundedIcon
                style={{ color: "rgb(25,118,210)", fontWeight: "bold" }}
              />
            </IconButton>
          </Tooltip>
          <Tooltip title="Move to Next Order">
            <IconButton onClick={goToNextOrder}>
              <ArrowForwardRoundedIcon
                style={{ color: "rgb(25,118,210)", fontWeight: "bold" }}
              />
            </IconButton>
          </Tooltip>
        </div>
      </Box>

      {labOrderData !== null && (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mt: 1, mb: 1, flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            {labOrderData.invoiceNumber?.trim() && (
              <span>
                Invoice #{" "}
                <a
                  style={{ color: "rgb(25,118,210)", fontWeight: "bold", cursor: "pointer" }}
                  onClick={() => handleViewInvoiceClick(labOrderData.invoiceNumber)}
                >
                  {labOrderData.invoiceNumber.trim()}
                </a>
              </span>
            )}
            <Chip
              label={labOrderData.willRushPay ? "Rush" : "No Rush"}
              size="small"
              color={labOrderData.willRushPay ? "error" : "default"}
              variant={labOrderData.willRushPay ? "filled" : "outlined"}
            />
            {labOrderData.turnAroundTime?.trim() && (
              <Chip
                label={`TaT: ${labOrderData.turnAroundTime.trim()} days`}
                size="small"
                color="info"
                variant="outlined"
              />
            )}
          </Box>
          <Button
            size="small"
            onClick={() => setActionsExpanded(!actionsExpanded)}
            endIcon={actionsExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ textTransform: "none" }}
          >
            Actions
          </Button>
        </Box>
      )}

      {labOrderData !== null && (
        <LabOrderActions
          orderNumber={labOrderData.orderNumber}
          orderType={labOrderData.clientType}
          accountNumber={labOrderData.accountNumber}
          aiSummary={labOrderData.aiSummary}
          aiSummaryReviewed={labOrderData.aiSummaryReviewed}
          expanded={actionsExpanded}
          onCheckInSubmitted={() => getLabOrder(orderNumber)}
        />
      )}
      {/* {labOrderData !== null && <LabOrderOverview order={labOrderData} />}
      {labOrderData !== null && <OrderBillingView order={labOrderData} />}
      {labOrderData !== null && <OrderReportingView order={labOrderData} />} */}
      {/* Line every section up with the page title and give all section headings one size,
          smaller than the 1.4rem page title. Scoped here because these tables are shared with
          View Lab Order and Edit Invoice. !important is needed only where the shared
          attachments card sets inline styles (margin, heading color). */}
      <Box
        sx={{
          "& > .MuiPaper-root": { px: 0, mx: "0 !important" },
          "& .MuiAccordionSummary-root, & .MuiAccordionDetails-root": { px: 0 },
          "& .MuiTypography-h5, & .MuiTypography-h6, & .MuiTypography-h13": {
            fontSize: "1.1rem",
          },
          "& .MuiTypography-h13": { display: "block", mb: 1, color: "inherit !important" },
        }}
      >
        {labOrderData !== null && <EditOrderInfo order={labOrderData} onUpdate={handleOrderUpdate} />}
        {labOrderData !== null && (
          <SamplesTable
            samples={labOrderData.samples}
            accountNumber={labOrderData.accountNumber}
            orderNumber={labOrderData.orderNumber}
          />
        )}
        {labOrderData !== null && (
          <ResultsTable
            results={labOrderData.results}
            orderNumber={orderNumber}
            accountNumber={labOrderData.accountNumber}
            onOrderUpdated={handleOrderFieldsChanged}
          />
        )}
        {orderNumber !== null && <OrderAttachmentsTable orderNumber={orderNumber} />}
      </Box>
      {/* {orderNumber !== null && <EditResultsTable orderNumber={orderNumber} />} */}

      <CommonLoader loading={loading} />
    </div>
  );
};

export default EditLabOrder;
